// apps/client/src/assets/builders/fish.ts — parametric fish (spec §8.3): body by FishLook.body, vertex-coloured belly, tail, fins, eyes, pattern
import * as THREE from 'three';
import { getItem, type FishEntry, type FishLook } from '@whiskerton/content';
import { toyMaterial } from '../../characters/doll';
import { bakeMeshes } from '../../world/bake';
import type { CodeBuilder } from '../service';

const BODY: Record<FishLook['body'], readonly [number, number, number]> = {
  slim: [1, 0.32, 0.22], round: [1, 0.62, 0.32], long: [1, 0.24, 0.2], flat: [1, 0.14, 0.62], eel: [1, 0.12, 0.12],
};
/** World length (tiles) of a fish of `cm` centimetres: minnows stay visible, giants stay holdable. */
export const fishScale = (cm: number) => THREE.MathUtils.clamp(0.16 + cm / 160, 0.2, 1.6);
const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };

/** How strongly the scale colour tints the back (it was a 0.28-opacity shell over the upper half of the body). */
const SCALE_TINT = 0.28;
/** Base on the back, belly underneath; with `scales`, the back is tinted by the scale colour in a soft scalloped pattern. */
function bellyColours(geo: THREE.BufferGeometry, base: string, belly: string, scales: string | null): void {
  const pos = geo.getAttribute('position'), a = new THREE.Color(base), b = new THREE.Color(belly), s = scales ? new THREE.Color(scales) : null, c = new THREE.Color();
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    c.copy(a);
    if (s) c.lerp(s, SCALE_TINT * THREE.MathUtils.smoothstep(y, -0.01, 0.03) * (0.75 + 0.25 * Math.cos(pos.getX(i) * 40) * Math.cos(y * 40)));
    c.lerp(b, THREE.MathUtils.smoothstep(-y, -0.02, 0.12)); c.toArray(col, i * 3);
  }
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
}

export function buildFish(look: FishLook, lengthCm = look.length, seed = 'fish'): THREE.Group {
  const g = new THREE.Group(); g.name = 'fish';
  const [sx, sy, sz] = BODY[look.body];
  let body: THREE.Mesh;
  if (look.body === 'eel') {
    const pts = [-0.5, -0.25, 0, 0.25, 0.5].map((x, i) => new THREE.Vector3(x, 0, Math.sin(i * 1.4) * 0.06));
    body = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 28, 0.06, 8, false), toyMaterial(look.base));
  } else {
    const geo = new THREE.SphereGeometry(0.5, 20, 14); geo.scale(sx, sy, sz);
    bellyColours(geo, look.base, look.belly, look.pattern === 'scales' ? look.patternColor : null);
    body = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0 }));
  }
  body.name = 'body';
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.26, 4).rotateZ(Math.PI / 2).scale(1, 1.1, 0.22), toyMaterial(look.fin));
  tail.name = 'tail'; tail.position.x = -0.6;
  const dorsal = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.2, 4).scale(1.6, 1, 0.2), toyMaterial(look.fin));
  dorsal.position.set(0.02, sy * 0.48, 0);
  const eyes = new THREE.Group();
  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.035, 10, 8), toyMaterial('#2E2C2A'));
    eye.position.set(0.36, sy * 0.12, side * sz * 0.4); eyes.add(eye);
  }
  const pattern = new THREE.Group(); pattern.name = 'pattern';
  const h = hash(seed);
  if (look.pattern === 'spots') {
    for (let i = 0; i < 5 + (h % 4); i++) {
      const t = ((h >>> (i * 3)) % 100) / 100, a = 0.4 + (((h >>> (i * 5)) % 60) / 100);
      const spot = new THREE.Mesh(new THREE.SphereGeometry(0.045, 8, 6).scale(1, 1, 0.4), toyMaterial(look.patternColor));
      spot.position.set(-0.32 + t * 0.62, Math.sin(a) * sy * 0.46, Math.cos(a) * sz * 0.46 * (i % 2 ? 1 : -1));
      pattern.add(spot);
    }
  } else if (look.pattern === 'stripes') {
    for (let i = 0; i < 4; i++) {
      const band = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.018, 6, 20).scale(1, sy, sz).rotateY(Math.PI / 2), toyMaterial(look.patternColor));
      band.position.x = -0.24 + i * 0.16; band.scale.setScalar(Math.cos((band.position.x / 0.5) * (Math.PI / 2)) * 0.98); pattern.add(band);
    }
  }                                                       // 'scales' is painted into the body's vertex colours (bellyColours)
  g.add(body, tail, dorsal, eyes, pattern);
  const L = fishScale(lengthCm);
  g.scale.setScalar(L);
  g.userData.lengthUnits = L;
  g.traverse((o) => { (o as THREE.Mesh).castShadow = true; });
  return g;
}
/** A fish model (buildFish's group, or the asset service's wrapper round it) as two draws: everything but the tail merged into one
 *  vertex-coloured 'body' mesh, and the 'tail' (its own mesh, so it can still wag about its root). Contact blobs are dropped. The
 *  root's own transform is not baked in: the returned group starts at identity, in the root's space (spec §8.7). */
export function bakeFish(model: THREE.Object3D): THREE.Group {
  const out = new THREE.Group(); out.name = 'fish';
  const tail = model.getObjectByName('tail') as THREE.Mesh | undefined;
  for (const m of bakeMeshes(model, { skip: (x) => x === tail })) { m.name = 'body'; out.add(m); }
  if (tail) {
    // Its own copy of the tail's geometry, marked own like the baked body, so whoever frees the baked fish frees it too (a code
    // builder's model is fresh per build: nobody else holds it). The material is a shared toy one.
    const t = new THREE.Mesh(tail.geometry.clone(), tail.material);
    t.name = 'tail'; t.userData.own = true;
    model.matrixWorld.clone().invert().multiply(tail.matrixWorld).decompose(t.position, t.quaternion, t.scale);
    out.add(t);
  }
  out.userData.lengthUnits = model.userData.lengthUnits;
  return out;
}
export const builder: CodeBuilder = {
  id: 'fish',
  build(params) {
    const e = getItem(String(params.catalogId)) as FishEntry;
    return buildFish(e.look, typeof params.size === 'number' ? params.size : e.look.length, e.id);
  },
};
